import * as Hammer from "hammerjs";
import * as Logger from "js-logger";
import * as PIXI from 'pixi.js';
import { Signal } from "signals";
import { AssetConfigSchema } from "slotworks/config/asset-config-schema";
import DeviceInfo from "slotworks/utils/device-info";
import Point from "slotworks/utils/geom/point";
import revolverModel from "slotworks/integration/revolver/model/revolver-model";
import { port } from "_debugger";

const iNoBounce = require("inobounce");

declare const __DEBUG__: boolean;

export enum Orientation {
    LANDSCAPE,
    PORTRAIT
}

class CanvasManager {

    public stage: PIXI.Container;

    /**
     * Theoretical width of the stage (doesn't include pixel density or css scaling)
     */
    public stageWidth: number;
    /**
     * Theoretical height of the stage (doesn't include pixel density or css scaling)
     */
    public stageHeight: number;

    public nativeWidth: number;
    public nativeHeight: number;

    /**
     * Pixel density pixi will render at. Shouldn't really need to access this as pixi will handle everything automatically
     */
    public resolution: number;

    public renderer: PIXI.Renderer; // | PIXI.CanvasRenderer;
    public canvas: HTMLCanvasElement;

    /**
     * For gestures
     */
    public hammer: HammerManager;

    public headerDOMContainer: HTMLDivElement;
    public footerDOMContainer: HTMLDivElement;
    public portraitDOMContainer: HTMLDivElement;
    public landscapeDOMContainer: HTMLDivElement;

    public orientation: Orientation;
    public onOrientationChange: Signal;

    private portraitEnabled: boolean = true;
    private landscapeScale: number = 1;
    private portraitScale: number = 1;
    private landscapeAnchor: Point = new Point(0.5, 0.5);
    private portraitAnchor: Point = new Point(0.5, 0.5);

    private isGameLoaded: boolean;
    private iosEnableZoom: boolean;

    private assetsConfig: AssetConfigSchema;
    private portraitString: string;

    public stageOffsetX: number;
    public stageOffsetY: number;

    /**
     * Creates a pixi renderer (WebGL with canvas fallback), an HTML canvas element and a pixi stage container
     * Uses graphics config to intialize factory methods
     * Starts render loop (requestAnimationFrame)
     */
    public init(assetConfig: AssetConfigSchema) {
        this.assetsConfig = assetConfig; // todo

        this.onOrientationChange = new Signal();

        // Show the document body
        document.body.style.display = "initial";

        this.stageOffsetX = 0;
        this.stageOffsetY = 0;

        // Delete root elements which aren't this layout
        for (let i = document.body.childNodes.length - 1; i >= 0; i--) {
            const element = document.body.childNodes.item(i) as HTMLElement;
            if (element.id !== DeviceInfo.layoutMain && element.id !== "all") {
                document.body.removeChild(element);
            }
        }
        this.portraitString = DeviceInfo.layoutMain === "mobilev2" ? "portraitv2" : "portrait";
        
        this.headerDOMContainer = document.getElementById("headerDOMContainer") as HTMLDivElement;
        this.footerDOMContainer = document.getElementById("footerDOMContainer") as HTMLDivElement;
        this.portraitDOMContainer = document.getElementById(this.portraitString) as HTMLDivElement;
        this.landscapeDOMContainer = document.getElementById("landscape") as HTMLDivElement;

        const layoutConfig = assetConfig.layouts[DeviceInfo.layoutId];

        const nativePixelDensity = layoutConfig.settings.nativePixelRatio;

        // Initialize pixi / canvas
        this.resolution = Math.round(DeviceInfo.pixelDensity);

        // Some devices may only have a 1x pixel density, but still possess enough pixels
        // to render higher scale graphics for this layout
        const width1x = layoutConfig.settings.nativeWidth / nativePixelDensity;
        const deviceWidth = Math.max(screen.width, screen.height);
        const psuedoPixelRatio = Math.round(deviceWidth / width1x);

        if (this.resolution < psuedoPixelRatio) {
            this.resolution = psuedoPixelRatio;

            this.log("Device pixel density is " + DeviceInfo.pixelDensity + " but upgraded assets to " + this.resolution);
        }

        this.resolution = Math.min(this.resolution, nativePixelDensity);

/////////////////////////
        if (DeviceInfo.forcePixelDensity && __DEBUG__) {
            this.resolution = DeviceInfo.forcePixelDensity;
            this.log("Forcing pixel density to " + DeviceInfo.forcePixelDensity);
        }
//////////////////

        // Give entire document class to determine pixel density (used for CSS psuedo queries)
        document.documentElement.classList.toggle("x" + this.resolution, true);

        // Give entire document class to tell it it's an iPhone 5,
        // and requires special treatment (to compensate for 44px dead zone at footer)
        if (DeviceInfo.isIphone5()) {
            document.documentElement.classList.toggle("iPhone5", true);
        }

        this.nativeWidth = layoutConfig.settings.nativeWidth;
        this.nativeHeight = layoutConfig.settings.nativeHeight;

        this.stageWidth = this.nativeWidth;
        this.stageHeight = this.nativeHeight;

        // this.renderer = PIXI.autoDetectRenderer(this.stageWidth, this.stageHeight, {
        //     transparent: true,
        // //    backgroundColor: 0xffffff,
        //     resolution: this.resolution / nativePixelDensity,        
        //     roundPixels: false
        // });
        this.renderer = PIXI.autoDetectRenderer({
            width: this.stageWidth, 
            height: this.stageHeight,
            transparent: true,
            resolution: this.resolution / nativePixelDensity,        
            // roundPixels: false
        });

        this.canvas = this.renderer.view;
        this.canvas.id = "canvas";
        document.body.appendChild(this.canvas);

        this.stage = new PIXI.Container();

        window.addEventListener("resize", (event) => this.resize(event));
        window.addEventListener("load", (event) => this.resize(event));
        window.addEventListener("orientationchange", (event) => this.resize(event));

        this.resize();

        const resolutionString = this.stageWidth + "x" + this.stageHeight + "@" + this.resolution + "x (" + this.renderer.width + "x" + this.renderer.height + "), ";
        this.log("Canvas created at " + resolutionString);

        // Gesture listeners
        this.hammer = new Hammer.Manager(this.canvas);

        if (!DeviceInfo.isSamsungBrowser() && !DeviceInfo.isAppleDevice()) {
            this.canvas.addEventListener("touchend", () => this.requestFullScreen());

            document.onclick = () => this.requestFullScreen();
        }

        // Apple hacks / fixes
        if (DeviceInfo.isAppleDevice()) {
            // iOS hack to force honoring of user-scale
            document.addEventListener("touchmove", (moveEvent: any) => {
                if (!this.iosEnableZoom && moveEvent.scale !== 1) {
                    moveEvent.preventDefault();
                }
            }, false);
            // iOS hack to prevent double tap zooming
            let lastTouchEnd = 0;
            document.addEventListener("touchend", (touchEvent: any) => {
                const now = (new Date()).getTime();
                if (now - lastTouchEnd <= 500) {
                    touchEvent.preventDefault();
                }
                lastTouchEnd = now;
            }, false);
        }
    }

    public setLandscapeScale(scale: number, anchor?: Point) {
    //    this.landscapeScale = scale;
        if (anchor) {
            this.landscapeAnchor = anchor;
        }
        Logger.log("set landscape scale", scale);
        this.resize();
    }

    public setPortraitEnabled(enabled: boolean) {
        this.portraitEnabled = enabled;
    }

    public setPortraitScale(scale: number, anchor?: Point) {
        this.portraitScale = scale;
        if (anchor) {
            this.portraitAnchor = anchor;
        }
        Logger.log("set portrait scale", scale);
        this.resize();
    }

    public setLeftHanded(leftHanded: boolean) {
        document.documentElement.classList.toggle("leftHanded", leftHanded);
    }

    public gameLoaded() {
        this.isGameLoaded = true;
    }

    private resize(event?: any) {
        let canvasDOMContainer: HTMLElement;
        let zoomLevel: number;
        let anchor: Point;        
        const landscape:boolean = this.getScreenWidth() > this.getScreenHeight() || !this.portraitEnabled;        

        DeviceInfo.orientationChanged(landscape);
        this.resizeRendererToOrientation(landscape);
        document.body.classList.toggle("portraitv2", false);
        document.body.classList.toggle("portrait", false);
        // Set orientation
        if (this.getScreenWidth() > this.getScreenHeight() || !this.portraitEnabled) {
            document.body.classList.toggle("portraitv2", false);
            document.body.classList.toggle("portrait", false);
            document.body.classList.toggle("landscape", true);

            if (this.portraitDOMContainer) {
                this.portraitDOMContainer.style.display = "none";
            }
            if (this.landscapeDOMContainer) {
                this.landscapeDOMContainer.style.display = "block";
            }

            canvasDOMContainer = document.querySelector("#landscape #canvasDOMContainer") as HTMLElement;
            zoomLevel = this.landscapeScale;
            anchor = this.landscapeAnchor;
            this.orientation = Orientation.LANDSCAPE;
        } else {
            document.body.classList.toggle(this.portraitString, true);
            document.body.classList.toggle("landscape", false);

            if (this.portraitDOMContainer) {
                this.portraitDOMContainer.style.display = "block";
            }
            if (this.landscapeDOMContainer) {
                this.landscapeDOMContainer.style.display = "none";
            }
            canvasDOMContainer = document.querySelector(`#${this.portraitString} #canvasDOMContainer`) as HTMLElement;

            zoomLevel = this.portraitScale;
            anchor = this.portraitAnchor;
            this.orientation = Orientation.PORTRAIT;
        }
        
        // Resize canvas including zoom
        this.resizeElement(this.canvas, zoomLevel, anchor);
        
        if (canvasDOMContainer) {
            this.resizeElement(canvasDOMContainer, zoomLevel, anchor, true);
        }
        this.onOrientationChange.dispatch(this.orientation);
        // Footer DOM container
        if (canvasDOMContainer && this.footerDOMContainer) {
            let height = this.getScreenHeight() - parseFloat(canvasDOMContainer.style.height) - parseFloat(canvasDOMContainer.style.marginTop);
            this.footerDOMContainer.style.width = this.getScreenWidth() + "px";            
            this.footerDOMContainer.style.height = (height > 0 ? height : 0) + "px";                        
            this.footerDOMContainer.style.marginTop = this.roundDownForDensity(this.getScreenHeight() - parseFloat(this.footerDOMContainer.style.height)) + "px";            
        }
        
        if (window && window.scrollTo) {
            window.scrollTo(0, 0);
        }

        this.scrollPrompt();
    }

    public resizeRendererToOrientation(landscape:boolean, offsetTop?: number) {
        const layoutConfig = this.assetsConfig.layouts[DeviceInfo.layoutId];

        const nativePixelDensity = layoutConfig.settings.nativePixelRatio;

        // Initialize pixi / canvas
        this.resolution = Math.round(DeviceInfo.pixelDensity);

        // Some devices may only have a 1x pixel density, but still possess enough pixels
        // to render higher scale graphics for this layout
        const width1x = layoutConfig.settings.nativeWidth / nativePixelDensity;
        const deviceWidth = Math.max(screen.width, screen.height);
        const psuedoPixelRatio = Math.round(deviceWidth / width1x);

        if (this.resolution < psuedoPixelRatio) {
            this.resolution = psuedoPixelRatio;

            this.log("Device pixel density is " + DeviceInfo.pixelDensity + " but upgraded assets to " + this.resolution);
        }

        this.resolution = Math.min(this.resolution, nativePixelDensity);

        this.nativeWidth = layoutConfig.settings.nativeWidth;
        this.nativeHeight = layoutConfig.settings.nativeHeight;

        this.stageWidth = this.nativeWidth;
        this.stageHeight = this.nativeHeight;

        if(!DeviceInfo.isDesktop && !landscape) {
            const width = this.getScreenWidth() * this.portraitScale;
            const height = offsetTop ? this.getScreenHeight() - offsetTop : this.getScreenHeight();

            const sratio = this.stageWidth / this.stageHeight;
            const ratio = width / height;

            let newWidth;
            let newHeight;
            if(sratio < ratio) {
                newHeight = this.stageHeight;
                newWidth = ratio * this.stageHeight;                
            } else {
                newWidth = this.stageWidth;
                newHeight = this.stageWidth * height / width;
            }

            this.stageOffsetX = (newWidth - this.stageWidth)/2;
            this.stageOffsetY = (newHeight - this.stageHeight)/2;

            this.stageWidth = newWidth;
            this.stageHeight = newHeight;           
        } else {
            this.stageOffsetX = 0;
            this.stageOffsetY = 0;
            this.renderer.resize(this.stageWidth, this.stageHeight); 
        }
        this.stage.position.set(this.stageOffsetX, this.stageOffsetY);

        this.renderer.resize(this.stageWidth, this.stageHeight);        
    }

    public resizeElement(element: HTMLElement, zoomLevel: number = 1, anchor: Point, limitToScreen: boolean = false, maintainAspect: boolean = true) {
        const elementWidth = this.stageWidth;
        const elementHeight = this.stageHeight;

        const viewPortWidth = this.getScreenWidth() * zoomLevel;
        const viewPortHeight = this.getScreenHeight() * zoomLevel;

        let scaleX = viewPortWidth / elementWidth;
        let scaleY = viewPortHeight / elementHeight;

        if (maintainAspect) {
            const scale = Math.min(scaleX, scaleY);
            scaleX = scale;
            scaleY = scale;
        }

        let scaledWidth = (elementWidth * scaleX);
        let scaledHeight = (elementHeight * scaleY);

        if (limitToScreen) {
            scaledWidth = Math.min(scaledWidth, this.getScreenWidth());
            scaledHeight = Math.min(scaledHeight, this.getScreenHeight());
        }

        element.style.width = this.roundUpForDensity(scaledWidth) + "px";
        element.style.height = this.roundUpForDensity(scaledHeight) + "px";

        const marginX = this.roundDownForDensity((this.getScreenWidth() - scaledWidth) * anchor.x);
        const marginY = this.roundDownForDensity((this.getScreenHeight() - scaledHeight) * anchor.y);
        const padding = "0";
        const margin = marginY + "px " + marginX + "px";
        const fontScale = Math.min(scaledWidth / this.nativeWidth, scaledHeight / this.nativeHeight) * 100;

        element.style.padding = padding;
        element.style.margin = margin;
        element.style.display = "block";
        element.style.fontSize = fontScale.toString() + "px";
    }

    private roundUpForDensity(n: number) {
        return Math.ceil(n / this.resolution) * this.resolution;
    }

    private roundDownForDensity(n: number) {
        return Math.floor(n / this.resolution) * this.resolution;
    }

    private requestFullScreen() {
        if (!DeviceInfo.isDesktop && !revolverModel.isFullscreenDisabled()) {
            const el: any = document.body;

            // Supports most browsers and their versions.
            const requestMethod = el.requestFullScreen || el.webkitRequestFullScreen || el.mozRequestFullScreen || el.msRequestFullScreen;

            if (requestMethod) {

                // Native full screen.
                requestMethod.call(el);
            }
        }
    }

    private scrollPrompt() {
        if (DeviceInfo.isAppleDevice() && this.isGameLoaded) {
            this.iosEnableZoom = false;

            if (window.innerHeight < this.getScreenHeight()) {
                this.iosEnableZoom = true;
                document.getElementById("iosScroll").classList.toggle("visible", true);
                document.getElementById("iosScroll").classList.toggle("hidden", false);
                document.body.classList.toggle("noOverflow", false);
                iNoBounce.disable();
            } else {
                document.getElementById("iosScroll").classList.toggle("visible", false);
                document.getElementById("iosScroll").classList.toggle("hidden", true);
                document.body.classList.toggle("noOverflow", true);
                iNoBounce.enable();
            }
        }
    }

    private getScreenWidth() {
        return DeviceInfo.isAppleDevice() ? document.documentElement.clientWidth : window.innerWidth;
    }

    private getScreenHeight() {
        return DeviceInfo.isAppleDevice() ? document.documentElement.clientHeight : window.innerHeight;
    }

    /**
     * Log a message with colored label
     *
     * @param message {string}
     */
    private log(message: string) {
        Logger.info("%c Canvas Manager ", "background: #f96; color: #fff", message);
    }
}

export default new CanvasManager();



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/graphics/canvas/canvas-manager.ts